import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Comment, ListItem } from '@travel-planner/shared';
import { api } from '../api/client';
import { itemsKey } from './useListItems';

export function useVote(tripId: string, countryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, value }: { itemId: string; value: boolean }) =>
      api.post<ListItem>(`/trips/${tripId}/items/${itemId}/vote`, { value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: itemsKey(tripId, countryId) }),
  });
}

export const commentsKey = (tripId: string, itemId: string) =>
  ['comments', tripId, itemId] as const;

export function useComments(tripId: string, itemId: string, enabled: boolean) {
  return useQuery<Comment[]>({
    queryKey: commentsKey(tripId, itemId),
    queryFn: () => api.get<Comment[]>(`/trips/${tripId}/items/${itemId}/comments`),
    enabled,
  });
}

export function useAddComment(tripId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post<Comment>(`/trips/${tripId}/items/${itemId}/comments`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentsKey(tripId, itemId) }),
  });
}

export function useDeleteComment(tripId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      api.del<void>(`/trips/${tripId}/items/${itemId}/comments/${commentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: commentsKey(tripId, itemId) }),
  });
}
